import { ChangeEvent, useEffect, useState } from "react";
import {
    Card,
    Col,
    Container,
    Row,
    Accordion,
    Toast,
    ToastContainer,
} from "react-bootstrap";
import { useParams } from "react-router-dom";
import { useAgentContext } from "../../context/agent";
import { useMutation, useQueryClient, useQuery } from "react-query";
import {
    updateAgent,
    updateAgentAvatar,
    updateAgentLauncher,
    getAgentPredefinedTools,
    managePredefinedTool,
    addApiTool,
    updateApiTool,
    getApiTools,
    deleteApiTool,
} from "../../api/agent";
import { useOrgContext } from "../../context/org";
import Chatbot from "./ChatBox";
import {
    FaCog,
    FaSearch,
    FaEnvelope,
    FaPhone,
    FaCheckCircle,
    FaExclamationTriangle,
} from "react-icons/fa";
import "./style.css";

// Import the new components
import BasicConfiguration from "./BasicConfiguration";
import AppearanceConfiguration from "./AppearanceConfiguration";
import ToolsConfiguration, { APIToolConfig } from "./ToolsConfiguration";
import ApiToolModal from "./ApiToolModal";
import { nanoid } from "nanoid";

interface ParameterConfig {
    value: string;
    required: boolean;
    type: "user-input" | "system-value";
    description?: string;
}

interface PredefinedTool {
    id: string;
    name: string;
    description: string;
    enabled: boolean;
}

function Configure() {
    const [systemPrompt, setSystemPrompt] = useState("");
    const [greeting, setGreeting] = useState("");
    const [displayName, setDisplayName] = useState("");
    const [isPrivate, setIsPrivate] = useState(false);
    const [hidePoweredBy, setHidePoweredBy] = useState(false);

    const [selectedAvatarFile, setSelectedAvatarFile] = useState<File | null>(
        null,
    );
    const [selectedLauncherFile, setSelectedLauncherFile] =
        useState<File | null>(null);

    const [primaryColor, setPrimaryColor] = useState("#212529");
    const [avatarImage, setAvatarImage] = useState("");
    const [launcherImage, setLauncherImage] = useState("");

    // Get route params first
    const { orgId, agentId } = useParams();

    // Tools state
    const [showToolModal, setShowToolModal] = useState(false);
    const [editingTool, setEditingTool] = useState<APIToolConfig | null>(null);

    // Fetch predefined tools from API
    const { data: predefinedTools = [], isLoading: isPredefinedToolsLoading } =
        useQuery(
            ["predefinedTools", orgId, agentId],
            () => getAgentPredefinedTools(orgId!, agentId!),
            {
                enabled: !!orgId && !!agentId,
            },
        );

    // Fetch API tools from API
    const { data: apiTools = [], isLoading: isApiToolsLoading } = useQuery(
        ["apiTools", orgId, agentId],
        () => getApiTools(orgId!, agentId!),
        {
            enabled: !!orgId && !!agentId,
        },
    );

    const [newTool, setNewTool] = useState<APIToolConfig>({
        id: nanoid(),
        name: "",
        description: "",
        method: "GET",
        url: "",
        headers: {},
        params: {},
        data: {},
        responseType: "json",
    });

    // Toast notification state
    const [showToast, setShowToast] = useState(false);
    const [toastMessage, setToastMessage] = useState("");
    const [toastType, setToastType] = useState<"success" | "error">("success");

    // Toast notification helper
    const showToastMessage = (message: string, type: "success" | "error") => {
        setToastMessage(message);
        setToastType(type);
        setShowToast(true);
    };

    const baseURL = process.env.REACT_APP_API_URL!;

    const { getAgentById } = useAgentContext();
    const agent = getAgentById(agentId || "");

    const { membership, subscription } = useOrgContext();

    const allowed = ["owner", "admin", "editor"];
    const permission = !allowed.includes(membership?.role || "");

    const queryClient = useQueryClient();
    const { mutateAsync: updateAgentFn, isLoading } = useMutation(updateAgent, {
        onSuccess: (data) => {
            queryClient.invalidateQueries(["Agents", orgId]);
            showToastMessage("Configuration updated successfully!", "success");
        },
        onError: (error) => {
            showToastMessage(
                "Failed to update configuration. Please try again.",
                "error",
            );
        },
    });
    const { mutateAsync: updateAgentAvatarFn, isLoading: isLoadingAvatar } =
        useMutation(updateAgentAvatar, {
            onSuccess: (data) => {
                queryClient.invalidateQueries(["Agents", orgId]);
                showToastMessage("Avatar updated successfully!", "success");
            },
            onError: (error) => {
                showToastMessage(
                    "Failed to update avatar. Please try again.",
                    "error",
                );
            },
        });
    const { mutateAsync: updateAgentLauncherFn, isLoading: isLoadingLauncher } =
        useMutation(updateAgentLauncher, {
            onSuccess: (data) => {
                queryClient.invalidateQueries(["Agents", orgId]);
                showToastMessage(
                    "Launcher image updated successfully!",
                    "success",
                );
            },
            onError: (error) => {
                showToastMessage(
                    "Failed to update launcher image. Please try again.",
                    "error",
                );
            },
        });

    // Mutation for activating predefined tools
    const { mutateAsync: managePredefinedToolFn } = useMutation(
        managePredefinedTool,
        {
            onSuccess: () => {
                queryClient.invalidateQueries([
                    "predefinedTools",
                    orgId,
                    agentId,
                ]);
                showToastMessage("Tool updated successfully!", "success");
            },
            onError: (error) => {
                showToastMessage(
                    "Failed to update tool. Please try again.",
                    "error",
                );
            },
        },
    );

    // Mutations for API tools CRUD operations
    const { mutateAsync: addApiToolFn, isLoading: isAddingApiTool } = useMutation(
        addApiTool,
        {
            onSuccess: () => {
                queryClient.invalidateQueries(["apiTools", orgId, agentId]);
                showToastMessage("API tool added successfully!", "success");
                setShowToolModal(false);
                setNewTool({
                    id: nanoid(),
                    name: "",
                    description: "",
                    method: "GET",
                    url: "",
                    headers: {},
                    params: {},
                    data: {},
                    responseType: "json",
                });
            },
            onError: (error) => {
                showToastMessage(
                    "Failed to add API tool. Please try again.",
                    "error",
                );
            },
        },
    );

    const { mutateAsync: updateApiToolFn, isLoading: isUpdatingApiTool } = useMutation(
        updateApiTool,
        {
            onSuccess: () => {
                queryClient.invalidateQueries(["apiTools", orgId, agentId]);
                showToastMessage("API tool updated successfully!", "success");
                setShowToolModal(false);
                setEditingTool(null);
            },
            onError: (error) => {
                showToastMessage(
                    "Failed to update API tool. Please try again.",
                    "error",
                );
            },
        },
    );

    const { mutateAsync: deleteApiToolFn, isLoading: isDeletingApiTool } = useMutation(
        deleteApiTool,
        {
            onSuccess: () => {
                queryClient.invalidateQueries(["apiTools", orgId, agentId]);
                showToastMessage("API tool deleted successfully!", "success");
            },
            onError: (error) => {
                showToastMessage(
                    "Failed to delete API tool. Please try again.",
                    "error",
                );
            },
        },
    );

    const handleUpdate = () => {
        if (!agent) {
            return;
        }
        updateAgentFn({
            id: agent.id,
            orgId: agent.organizationId,
            systemPrompt,
            greeting,
            displayName,
            isPrivate,
            hidePoweredBy,
        });
    };

    const handleLauncherFileChange = (e: ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file && file.type.startsWith("image/")) {
            setSelectedLauncherFile(file);
            const reader = new FileReader();
            reader.onloadend = () => {
                setLauncherImage(reader.result as string);
            };
            reader.readAsDataURL(file);
        } else {
            alert("Please select a valid image file.");
        }
    };

    const handleAvatarFileChange = (e: ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file && file.type.startsWith("image/")) {
            setSelectedAvatarFile(file);
            const reader = new FileReader();
            reader.onloadend = () => {
                setAvatarImage(reader.result as string);
            };
            reader.readAsDataURL(file);
        } else {
            alert("Please select a valid image file.");
        }
    };

    const updateAppearance = () => {
        if (!agent) {
            return;
        }

        try {
            if (primaryColor && primaryColor !== agent.primaryColor) {
                updateAgentFn({
                    id: agent.id,
                    orgId: agent.organizationId,
                    primaryColor,
                });
            }

            if (selectedAvatarFile) {
                const formData = new FormData();
                formData.append("agentId", agent.id);
                formData.append("orgId", agent.organizationId);
                formData.append("file", selectedAvatarFile);
                updateAgentAvatarFn({
                    orgId: agent.organizationId,
                    id: agent.id,
                    formData,
                });
                setSelectedAvatarFile(null);
            }

            if (selectedLauncherFile) {
                const formData = new FormData();
                formData.append("agentId", agent.id);
                formData.append("orgId", agent.organizationId);
                formData.append("file", selectedLauncherFile);
                updateAgentLauncherFn({
                    orgId: agent.organizationId,
                    id: agent.id,
                    formData,
                });
                setSelectedLauncherFile(null);
            }

            // Show success message if no files were uploaded (only color change)
            if (
                !selectedAvatarFile &&
                !selectedLauncherFile &&
                primaryColor &&
                primaryColor !== agent.primaryColor
            ) {
                showToastMessage(
                    "Primary color updated successfully!",
                    "success",
                );
            }
        } catch (error) {
            showToastMessage(
                "Failed to update appearance. Please try again.",
                "error",
            );
        }
    };

    // Tool management functions
    const togglePredefinedTool = async (toolId: string) => {
        if (!orgId || !agentId) return;

        try {
            await managePredefinedToolFn({
                orgId,
                agentId,
                toolId,
                enabled: !predefinedTools.find((tool) => tool.id === toolId)
                    ?.enabled,
            });
        } catch (error) {
            console.error("Failed to toggle predefined tool:", error);
        }
    };

    const toggleApiTool = async (toolId: string) => {
        const tool = apiTools.find((t) => t.id === toolId);
        if (!tool) return;

        try {
            await updateApiToolFn({
                ...tool,
                enabled: !tool.enabled,
            });
        } catch (error) {
            console.error("Failed to toggle API tool:", error);
        }
    };

    const handleAddApiTool = async () => {
        if (!newTool.name || !newTool.url) {
            showToastMessage("Please provide tool name and URL.", "error");
            return;
        }

        try {
            await addApiToolFn(newTool);
        } catch (error) {
            console.error("Failed to add API tool:", error);
        }
    };

    const handleUpdateApiTool = async () => {
        if (!editingTool) return;

        try {
            await updateApiToolFn(editingTool);
        } catch (error) {
            console.error("Failed to update API tool:", error);
        }
    };

    const handleDeleteApiTool = async (toolId: string) => {
        if (!orgId || !agentId) return;

        try {
            await deleteApiToolFn({
                orgId,
                agentId,
                toolId,
            });
        } catch (error) {
            console.error("Failed to delete API tool:", error);
        }
    };

    const handleEditApiTool = (tool: APIToolConfig) => {
        setEditingTool(tool);
        setNewTool(tool);
        setShowToolModal(true);
    };

    const handleCloseModal = () => {
        setShowToolModal(false);
        setEditingTool(null);
        setNewTool({
            id: nanoid(),
            name: "",
            description: "",
            method: "GET",
            url: "",
            headers: {},
            params: {},
            data: {},
            responseType: "json",
        });
    };

    const addKeyValuePair = (field: "headers" | "params" | "data") => {
        setNewTool((prev) => ({
            ...prev,
            [field]: {
                ...prev[field],
                "": {
                    value: "",
                    required: false,
                    type: "user-input",
                    description: "",
                },
            },
        }));
    };

    const updateKeyValuePair = (
        field: "headers" | "params" | "data",
        oldKey: string,
        newKey: string,
        config: ParameterConfig,
    ) => {
        setNewTool((prev) => {
            const newObj = { ...prev[field] };
            if (oldKey !== newKey) {
                delete newObj[oldKey];
            }
            newObj[newKey] = config;
            return { ...prev, [field]: newObj };
        });
    };

    const removeKeyValuePair = (
        field: "headers" | "params" | "data",
        key: string,
    ) => {
        setNewTool((prev) => {
            const newObj = { ...prev[field] };
            delete newObj[key];
            return { ...prev, [field]: newObj };
        });
    };

    useEffect(() => {
        if (agent) {
            setSystemPrompt(agent.systemPrompt);
            setGreeting(agent.greeting);
            setDisplayName(agent.displayName);
            setIsPrivate(agent.isPrivate);
            setHidePoweredBy(agent.hidePoweredBy);

            setPrimaryColor(agent.primaryColor);
            setAvatarImage(
                agent.avatarImageURL ? baseURL + agent.avatarImageURL : "",
            );
            setLauncherImage(
                agent.launcherImageURL ? baseURL + agent.launcherImageURL : "",
            );
        }
    }, [agent]);

    return (
        <>
            {!agent ? (
                <Container style={{ minHeight: "100vh", padding: "2rem 0" }}>
                    <div
                        style={{
                            backgroundColor: "#2d2d2d",
                            padding: "3rem",
                            borderRadius: "8px",
                            textAlign: "center",
                        }}
                    >
                        <h1 style={{ color: "#fff" }}>Agent not found</h1>
                    </div>
                </Container>
            ) : (
                <div style={{ minHeight: "100vh" }}>
                    {/* Header */}
                    <div
                        className="sticky-top"
                        style={{
                            backgroundColor: "#2d2d2d",
                            borderBottom: "1px solid #444",
                            padding: "1rem 0",
                        }}
                    >
                        <Container>
                            <div className="d-flex align-items-center">
                                <div className="bg-primary rounded p-2 me-3">
                                    <FaCog className="text-white" />
                                </div>
                                <div>
                                    <h4 className="mb-0 text-light">
                                        Configure Agent
                                    </h4>
                                    <small style={{ color: "#999" }}>
                                        Customize your AI agent's behavior,
                                        appearance, and tools
                                    </small>
                                </div>
                            </div>
                        </Container>
                    </div>

                    <Container fluid className="py-4">
                        <Row>
                            <Col lg={6} className="mb-4">
                                {/* Accordions for organized content */}
                                <Accordion
                                    defaultActiveKey={["0"]}
                                    alwaysOpen
                                    style={{ marginBottom: "1.5rem" }}
                                >
                                    {/* Basic Configuration */}
                                    <BasicConfiguration
                                        displayName={displayName}
                                        setDisplayName={setDisplayName}
                                        greeting={greeting}
                                        setGreeting={setGreeting}
                                        systemPrompt={systemPrompt}
                                        setSystemPrompt={setSystemPrompt}
                                        hidePoweredBy={hidePoweredBy}
                                        setHidePoweredBy={setHidePoweredBy}
                                        permission={permission}
                                        subscription={subscription}
                                        orgId={orgId!}
                                        isLoading={isLoading}
                                        onUpdate={handleUpdate}
                                    />

                                    {/* Appearance */}
                                    <AppearanceConfiguration
                                        primaryColor={primaryColor}
                                        setPrimaryColor={setPrimaryColor}
                                        avatarImage={avatarImage}
                                        setAvatarImage={setAvatarImage}
                                        launcherImage={launcherImage}
                                        setLauncherImage={setLauncherImage}
                                        selectedAvatarFile={selectedAvatarFile}
                                        setSelectedAvatarFile={
                                            setSelectedAvatarFile
                                        }
                                        selectedLauncherFile={
                                            selectedLauncherFile
                                        }
                                        setSelectedLauncherFile={
                                            setSelectedLauncherFile
                                        }
                                        handleAvatarFileChange={
                                            handleAvatarFileChange
                                        }
                                        handleLauncherFileChange={
                                            handleLauncherFileChange
                                        }
                                        agent={agent}
                                        baseURL={baseURL}
                                        permission={permission}
                                        isLoadingAvatar={isLoadingAvatar}
                                        isLoadingLauncher={isLoadingLauncher}
                                        onUpdateAppearance={updateAppearance}
                                    />

                                    {/* Tools & Integrations */}
                                    <ToolsConfiguration
                                        predefinedTools={predefinedTools}
                                        apiTools={apiTools}
                                        permission={permission}
                                        onTogglePredefinedTool={
                                            togglePredefinedTool
                                        }
                                        onToggleApiTool={toggleApiTool}
                                        onAddApiTool={() =>
                                            setShowToolModal(true)
                                        }
                                        onEditApiTool={handleEditApiTool}
                                        onDeleteApiTool={handleDeleteApiTool}
                                    />
                                </Accordion>
                            </Col>

                            {/* Chatbot Preview */}
                            <Col lg={6}>
                                <div
                                    className="sticky-top"
                                    style={{ top: "6rem" }}
                                >
                                    <Card
                                        style={{
                                            backgroundColor: "#2d2d2d",
                                            borderColor: "#444",
                                        }}
                                    >
                                        <Card.Header
                                            style={{
                                                backgroundColor: "#2d2d2d",
                                                borderBottom: "1px solid #444",
                                            }}
                                        >
                                            <h5 className="mb-0 text-light">
                                                Live Preview
                                            </h5>
                                            <small style={{ color: "#999" }}>
                                                See how your agent will look and
                                                behave
                                            </small>
                                        </Card.Header>
                                        <Card.Body
                                            style={{
                                                minHeight: "400px",
                                                display: "flex",
                                                alignItems: "center",
                                                justifyContent: "center",
                                            }}
                                        >
                                            <Chatbot
                                                orgId={agent.organizationId}
                                                agentId={agent.id}
                                                displayName={displayName}
                                                greeting={greeting}
                                                primaryColor={primaryColor}
                                                launcherImageURL={launcherImage}
                                                avatarImageURL={avatarImage}
                                                hidePoweredBy={hidePoweredBy}
                                            />
                                        </Card.Body>
                                    </Card>
                                </div>
                            </Col>
                        </Row>
                    </Container>

                    {/* API Tool Modal */}
                    <ApiToolModal
                        show={showToolModal}
                        onHide={handleCloseModal}
                        editingTool={editingTool}
                        newTool={newTool}
                        setNewTool={setNewTool}
                        onSave={handleAddApiTool}
                        onUpdate={handleUpdateApiTool}
                        addKeyValuePair={addKeyValuePair}
                        updateKeyValuePair={updateKeyValuePair}
                        removeKeyValuePair={removeKeyValuePair}
                    />

                    {/* Toast Notifications */}
                    <ToastContainer
                        position="top-end"
                        style={{
                            position: "fixed",
                            top: "20px",
                            right: "20px",
                            zIndex: 9999,
                        }}
                    >
                        <Toast
                            show={showToast}
                            onClose={() => setShowToast(false)}
                            delay={4000}
                            autohide
                            style={{
                                backgroundColor:
                                    toastType === "success"
                                        ? "#1a2e1a"
                                        : "#2e1a1a",
                                borderColor:
                                    toastType === "success"
                                        ? "#28a745"
                                        : "#dc3545",
                            }}
                        >
                            <Toast.Header
                                style={{
                                    backgroundColor:
                                        toastType === "success"
                                            ? "#1a2e1a"
                                            : "#2e1a1a",
                                    borderBottom: `1px solid ${toastType === "success" ? "#28a745" : "#dc3545"}`,
                                    color: "#fff",
                                }}
                            >
                                {toastType === "success" ? (
                                    <FaCheckCircle className="text-success me-2" />
                                ) : (
                                    <FaExclamationTriangle className="text-danger me-2" />
                                )}
                                <strong className="me-auto text-light">
                                    {toastType === "success"
                                        ? "Success"
                                        : "Error"}
                                </strong>
                            </Toast.Header>
                            <Toast.Body className="text-light">
                                {toastMessage}
                            </Toast.Body>
                        </Toast>
                    </ToastContainer>
                </div>
            )}
        </>
    );
}

export default Configure;
